import React, { useMemo, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import {
  DownloadDropdown,
  Dropdown,
  DropdownToggleType,
  InfoTooltip,
  Spinner
} from "../../Components";
import ChartContainer from "../../Components/ChartContainer";
import AreaChart from "../../Components/Charts/AreaChart";
import DynamicModelOverview from "../../Components/DynamicModelOverview/DynamicModelOverview";
import WidgetContainer from "../../Components/WidgetContainer";
import ActualVsPredicted from "../../MMM/ModelResults/ActualVsPredicted";
import { SetError } from "../../redux/modals";
import { DATE_GROUPING_OPTIONS, DateGroupingKey } from "../../TVADCrossChannel/homePageConstants";
import { Brand25 } from "../../utils/colors";
import { dateToMddyy } from "../../utils/date-utils";
import {
  formatAxisSpend,
  formatSpend,
  FullForecastSelection,
  ModelSummaryData,
  OUTCOME_VARIABLE_LABELS,
  TrainTestData
} from "../forecastingConstants";
import {
  formatModelOverviewData,
  getCombinedChannelPlatform,
  getKpiFormatter
} from "../forecastingUtils";

interface ModelResultsProps {
  selectedTrainTestData: TrainTestData[] | undefined;
  modelSummaryData: ModelSummaryData | undefined;
  selectedForecast: FullForecastSelection;
  showWIPFeatures?: boolean;
  chartDateGrouping: DateGroupingKey;
  setChartDateGrouping: (newVal: DateGroupingKey) => void;
  setError: SetError;
}

const ModelResults = ({
  selectedForecast,
  selectedTrainTestData,
  modelSummaryData,
  showWIPFeatures = false,
  chartDateGrouping,
  setChartDateGrouping,
  setError,
}: RouteComponentProps & ModelResultsProps): JSX.Element => {
  const [kpiChartYAxisWidth, setKpiChartYAxisWidth] = useState(0);
  const [spendChartYAxisWidth, setSpendChartYAxisWidth] = useState(0);

  // Maintain horizontal alignment between both stacked charts
  const maxYAxisWidth = useMemo(() => Math.max(kpiChartYAxisWidth, spendChartYAxisWidth), [
    kpiChartYAxisWidth,
    spendChartYAxisWidth,
  ]);

  const formatKpi = useMemo(() => {
    return getKpiFormatter(selectedForecast.outcomeVar);
  }, [selectedForecast]);

  return (
    <WidgetContainer
      collapsible
      header={`How well does the model understand our ${getCombinedChannelPlatform(
        selectedForecast.channel,
        selectedForecast.platform
      )} business?`}
      subHeader={
        <>
          High out-of-sample prediction accuracy is indicative of reliable causal understanding.
          <InfoTooltip rightLabel="More info" size="sm">
            The model is estimated by splitting up the time series foldData into slices called
            “train” and “test.” The training foldData is used to estimate the relationships between
            inputs (media investments, contextual variables) and outputs (the KPI). The test
            foldData is used to evaluate the performance of the trained model “out of sample,” i.e.
            on observations it has not already encountered.
            <br />
            <br /> Out-of-sample predictive accuracy is a critical concept because it proxies what
            we ultimately care about, namely an understanding of likely KPI outputs given a
            combination of novel media inputs. See the out-of-sample statistics at right for
            measures of this concept.
          </InfoTooltip>
        </>
      }
    >
      {!selectedTrainTestData || selectedTrainTestData.length === 0 || !modelSummaryData ? (
        <div className="forecastingWidgetLoader modelResultsLoader">
          <Spinner size={50} />
        </div>
      ) : (
        <div className="forecastingModelOverview">
          <div className="left">
            <ChartContainer
              enableHoverDesign
              rightActions={
                showWIPFeatures ? <DownloadDropdown size="sm" onClickOptions={[]} /> : undefined
              }
              title={
                <>
                  {showWIPFeatures && (
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartDateGrouping}
                      options={DATE_GROUPING_OPTIONS}
                      onChange={option => {
                        // [DEV-18381] TODO: Support date group
                        setChartDateGrouping(option);
                        setError({
                          message: "Updating date grouping of forecasting charts not yet supported",
                        });
                      }}
                    />
                  )}
                  <div>
                    Historical {selectedForecast.channel}{" "}
                    {OUTCOME_VARIABLE_LABELS[selectedForecast.outcomeVar]}
                  </div>
                  <div className="headerText">(Actual vs Predicted)</div>
                </>
              }
            >
              <ActualVsPredicted
                data={selectedTrainTestData}
                dateGrouping={chartDateGrouping}
                legendValue={OUTCOME_VARIABLE_LABELS[selectedForecast.outcomeVar]}
                syncId={1}
                tooltipFormatter={formatKpi}
                yAxisFormatter={formatKpi}
                yAxisStaticWidth={maxYAxisWidth}
                setChartYAxisDynamicWidth={setKpiChartYAxisWidth}
              />
            </ChartContainer>
            <ChartContainer
              enableHoverDesign
              rightActions={
                showWIPFeatures ? <DownloadDropdown size="sm" onClickOptions={[]} /> : undefined
              }
              title={
                <>
                  {showWIPFeatures && (
                    <Dropdown
                      type={DropdownToggleType.WIDGET_TITLE}
                      value={chartDateGrouping}
                      options={DATE_GROUPING_OPTIONS}
                      onChange={option => {
                        // [DEV-18381] TODO: Support date group
                        setChartDateGrouping(option);
                        setError({
                          message: "Updating date grouping of forecasting charts not yet supported",
                        });
                      }}
                    />
                  )}
                  <div>Historical {selectedForecast.channel} Spend</div>
                </>
              }
            >
              <AreaChart
                data={selectedTrainTestData}
                areas={[
                  {
                    dataKey: "spend",
                    incrementality: true,
                    name: `${selectedForecast.channel} Spend`,
                  },
                ]}
                dateGrouping={chartDateGrouping}
                xAxisDataKey="date"
                xAxisTickFormatter={val => dateToMddyy(val)}
                yAxisTickFormatter={val => formatAxisSpend(val)}
                tooltipFormatter={val => formatSpend(val)}
                yAxisStaticWidth={maxYAxisWidth}
                setChartYAxisDynamicWidth={setSpendChartYAxisWidth}
                hardCodedColorName={`${selectedForecast.channel} Spend`}
                hardCodedColor={Brand25}
                incrementality={false}
                syncId={1}
              />
            </ChartContainer>
          </div>
          <div className="right">
            <DynamicModelOverview
              sections={formatModelOverviewData(
                modelSummaryData,
                selectedForecast.outcomeVar,
                selectedForecast.primaryBudget,
                selectedForecast.endDate
              )}
              rightActions={
                // [DEV-18380] TODO: Support download
                showWIPFeatures ? (
                  <DownloadDropdown size="sm" onClickOptions={[]} menuOptions={["XLSX", "CSV"]} />
                ) : undefined
              }
              height={835}
            />
          </div>
        </div>
      )}
    </WidgetContainer>
  );
};

export default ModelResults;
